import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import type { AppInput, AppRecord } from '@/lib/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: AppRecord | null;
}

function blank(): AppInput {
  return {
    name: '',
    androidPackage: '',
    iosBundleId: '',
    androidUrl: '',
    iosUrl: '',
    webUrl: '',
    description: '',
  };
}

function fromRecord(a: AppRecord): AppInput {
  return {
    name: a.name,
    androidPackage: a.androidPackage ?? '',
    iosBundleId: a.iosBundleId ?? '',
    androidUrl: a.androidUrl ?? '',
    iosUrl: a.iosUrl ?? '',
    webUrl: a.webUrl ?? '',
    description: a.description ?? '',
  };
}

function trimToUndefined(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

export function AppFormDialog({ open, onOpenChange, editing }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AppInput>(blank);

  useEffect(() => {
    if (open) setForm(editing ? fromRecord(editing) : blank());
  }, [open, editing]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: AppInput = {
        name: form.name.trim(),
        androidPackage: trimToUndefined(form.androidPackage),
        iosBundleId: trimToUndefined(form.iosBundleId),
        androidUrl: trimToUndefined(form.androidUrl),
        iosUrl: trimToUndefined(form.iosUrl),
        webUrl: trimToUndefined(form.webUrl),
        description: trimToUndefined(form.description),
      };
      if (editing) {
        const { data } = await api.patch<AppRecord>(`/v1/apps/${editing.id}`, payload);
        return data;
      }
      const { data } = await api.post<AppRecord>('/v1/apps', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.apps });
      toast({ title: editing ? 'App updated' : 'App created' });
      onOpenChange(false);
    },
    onError: () =>
      toast({
        title: 'Could not save app',
        description: 'Check that at least one URL has the matching package / bundle.',
        variant: 'destructive',
      }),
  });

  function update<K extends keyof AppInput>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.androidUrl && !form.iosUrl && !form.webUrl) {
      toast({
        title: 'Add at least one URL',
        description: 'Apps need at least one of Android / iOS / Web URL.',
        variant: 'destructive',
      });
      return;
    }
    save.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit app' : 'Register a new app'}</DialogTitle>
          <DialogDescription>
            The bundle ID / package name binds your publishable key to this app.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Acme Shop"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="androidPackage">Android package name</Label>
              <Input
                id="androidPackage"
                value={form.androidPackage}
                onChange={(e) => update('androidPackage', e.target.value)}
                placeholder="com.acme.shop"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="iosBundleId">iOS bundle ID</Label>
              <Input
                id="iosBundleId"
                value={form.iosBundleId}
                onChange={(e) => update('iosBundleId', e.target.value)}
                placeholder="com.acme.shop"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="androidUrl">Android store URL</Label>
            <Input
              id="androidUrl"
              value={form.androidUrl}
              onChange={(e) => update('androidUrl', e.target.value)}
              placeholder="https://play.google.com/store/apps/details?id=com.acme.shop"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="iosUrl">iOS store URL</Label>
            <Input
              id="iosUrl"
              value={form.iosUrl}
              onChange={(e) => update('iosUrl', e.target.value)}
              placeholder="https://apps.apple.com/app/id123456789"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="webUrl">Web landing URL</Label>
            <Input
              id="webUrl"
              value={form.webUrl}
              onChange={(e) => update('webUrl', e.target.value)}
              placeholder="https://acme.com"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Buy and sell locally."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create app'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
